import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut, Search, Check, X, MessageCircle, RefreshCw, CalendarClock,
  Wallet, Clock3, CircleCheck, Gift, TrendingUp, Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError, brl, NOTIFY_WHATSAPP } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prettyDate } from "@/lib/dates";
import BannerManager from "@/components/BannerManager";
import SpaceImageManager from "@/components/SpaceImageManager";
import HeroImageManager from "@/components/HeroImageManager";
import LogoManager from "@/components/LogoManager";
import BlocksManager from "@/components/BlocksManager";
import AgendaView from "@/components/AgendaView";
import ReviewsManager from "@/components/ReviewsManager";
import DayUseManager from "@/components/DayUseManager";
import UsersManager from "@/components/UsersManager";
import BarManager from "@/components/BarManager";
import BarOrdersPanel from "@/components/BarOrdersPanel";
import PageLogoManager from "@/components/PageLogoManager";
import FloatingMenuButton from "@/components/FloatingMenuButton";

const FILTERS = [
  { id: "hoje", label: "Hoje" },
  { id: "amanha", label: "Amanhã" },
  { id: "todas", label: "Todas" },
];

const STATUS_FILTERS = [
  { id: "todos", label: "Todas" },
  { id: "pendente", label: "Pendentes" },
  { id: "confirmada", label: "Aprovadas" },
  { id: "cancelada", label: "Canceladas" },
  { id: "creditada", label: "Creditadas" },
];

const STATUS_STYLE = {
  pendente: "bg-amber-100 text-amber-800",
  confirmada: "bg-emerald-100 text-emerald-800",
  cancelada: "bg-red-100 text-red-700",
  expirada: "bg-slate-200 text-slate-500",
  creditada: "bg-amber-100 text-amber-800",
};

const STATUS_LABEL = {
  pendente: "pendente",
  confirmada: "confirmada",
  cancelada: "cancelada",
  expirada: "expirada",
  creditada: "creditada ⭐",
};

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

function Stat({ icon, label, value, tint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`mb-2 inline-flex h-9 w-9 items-center justify-center rounded-xl ${tint}`}>
        {icon}
      </div>
      <p className="text-2xl font-black text-slate-900">{value}</p>
      <p className="text-xs font-medium text-slate-500">{label}</p>
    </div>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user, checking, logout } = useAuth();
  const isMainAdmin = user?.role === "admin";
  const [filter, setFilter] = useState("hoje");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ reservations: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const leavingRef = useRef(false);

  useEffect(() => {
    if (!checking && (!user || !(user.perms || []).includes("reservas")) && !leavingRef.current) navigate("/");
  }, [checking, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/reservations", { params: { filter, status: statusFilter, q } });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        navigate("/login");
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter, q, logout, navigate]);

  useEffect(() => {
    if (user && (user.perms || []).includes("reservas")) {
      const t = setTimeout(load, 250);
      return () => clearTimeout(t);
    }
  }, [user, load]);

  const act = async (id, action) => {
    const messages = {
      approve: "Reserva aprovada!",
      cancel: "Reserva cancelada.",
      credit: "Reserva cancelada e creditada! Esta pessoa tem horas para usar depois.",
      "use-credit": "Crédito utilizado! Reserva concluída como aprovada.",
      release: "Horário desocupado e liberado para novas reservas.",
    };
    try {
      await api.post(`/admin/reservations/${id}/${action}`);
      toast.success(messages[action] || "Feito.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const notify = (r) => {
    const msg =
      `*Reserva Dubal Esportes*%0A` +
      `Espaço: ${r.space_name}%0A` +
      `Cliente: ${r.name || "-"}%0A` +
      `Data: ${prettyDate(r.date)}%0A` +
      `Horário: ${r.time_label}%0A` +
      `Valor: ${brl(r.price)}%0A` +
      `Instagram: ${r.instagram || "-"}%0A` +
      `Telefone: ${r.phone}%0A` +
      `Status: ${r.status}`;
    window.open(`https://wa.me/${NOTIFY_WHATSAPP}?text=${msg}`, "_blank");
  };

  const stats = data.stats || {};
  const reservations = data.reservations || [];
  const revenueByMonth = data.revenue_by_month || [];
  const revenueTotal = data.revenue_total || 0;

  if (checking) return null;

  return (
    <div className="min-h-screen bg-[#F4F6F4]">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <div>
            <p className="font-display text-xl font-bold tracking-tight text-slate-900">
              Reservas &amp; Faturamento
            </p>
            <p className="text-xs text-slate-400">Gestão de reservas</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/mind" data-testid="back-to-admin" className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200">
              Voltar ao Admin
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              data-testid="admin-refresh"
              className="rounded-full border-slate-200"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                leavingRef.current = true;
                navigate("/login");
                logout();
              }}
              data-testid="admin-logout"
              className="rounded-full border-slate-200 text-slate-600"
            >
              <LogOut className="mr-1.5 h-4 w-4" /> Sair
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={<CalendarClock className="h-5 w-5 text-slate-600" />} tint="bg-slate-100" label="Reservas" value={stats.total ?? 0} />
          <Stat icon={<Clock3 className="h-5 w-5 text-amber-700" />} tint="bg-amber-100" label="Pendentes" value={stats.pendentes ?? 0} />
          <Stat icon={<CircleCheck className="h-5 w-5 text-emerald-700" />} tint="bg-emerald-100" label="Confirmadas" value={stats.confirmadas ?? 0} />
          <Stat icon={<Wallet className="h-5 w-5 text-[#E8611A]" />} tint="bg-orange-100" label="Faturamento" value={brl(stats.faturamento ?? 0)} />
        </div>

        {/* Controls */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`admin-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                  filter === f.id
                    ? "bg-[#1B7A45] text-white"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-[#1B7A45]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por telefone ou @"
              className="rounded-full border-slate-200 pl-9"
            />
          </div>
        </div>

        {/* Status filter */}
        <div className="mt-3 flex flex-wrap gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.id}
              data-testid={`admin-status-${s.id}`}
              onClick={() => setStatusFilter(s.id)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                statusFilter === s.id
                  ? "bg-[#E8611A] text-white"
                  : "border border-slate-200 bg-white text-slate-600 hover:border-[#E8611A]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="mt-5 space-y-3">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl bg-white" />
            ))
          ) : reservations.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
              <p className="font-semibold text-slate-500">Nenhuma reserva encontrada</p>
              <p className="mt-1 text-sm text-slate-400">Ajuste os filtros ou aguarde novas reservas.</p>
            </div>
          ) : (
            reservations.map((r) => (
              <div
                key={r.id}
                data-testid={`reservation-${r.id}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-display text-lg font-bold text-slate-900">
                        {r.space_name}
                      </span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[r.status]}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-slate-500">
                      {prettyDate(r.date)} · {r.time_label}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      {r.name && (
                        <span className="text-slate-700">
                          👤 <span className="font-semibold">{r.name}</span>
                        </span>
                      )}
                      <span className="text-slate-600">
                        📱 <span className="font-medium">{r.phone}</span>
                      </span>
                      {r.instagram && (
                        <a
                          href={`https://www.instagram.com/${r.instagram.replace(/^@/, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`instagram-link-${r.id}`}
                          className="text-[#E8611A] hover:underline"
                        >
                          📷 <span className="font-medium">{r.instagram}</span>
                        </a>
                      )}
                      <span className="font-bold text-[#E8611A]">{brl(r.price)}</span>
                    </div>
                  </div>
                </div>

                {isMainAdmin && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  {r.status !== "confirmada" && r.status !== "cancelada" && r.status !== "creditada" && (
                    <Button
                      data-testid={`approve-${r.id}`}
                      onClick={() => act(r.id, "approve")}
                      size="sm"
                      className="rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]"
                    >
                      <Check className="mr-1.5 h-4 w-4" /> Aprovar
                    </Button>
                  )}
                  {r.status !== "cancelada" && r.status !== "creditada" && (
                    <Button
                      data-testid={`credit-${r.id}`}
                      onClick={() => act(r.id, "credit")}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                    >
                      <Gift className="mr-1.5 h-4 w-4" /> Cancelar e Creditar
                    </Button>
                  )}
                  {r.status !== "cancelada" && r.status !== "creditada" && (
                    <Button
                      data-testid={`cancel-${r.id}`}
                      onClick={() => act(r.id, "cancel")}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                    >
                      <X className="mr-1.5 h-4 w-4" /> Cancelar
                    </Button>
                  )}
                  {["pendente", "confirmada", "creditada"].includes(r.status) && (
                    <Button
                      data-testid={`release-${r.id}`}
                      onClick={() => act(r.id, "release")}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    >
                      <Unlock className="mr-1.5 h-4 w-4" /> Desocupar
                    </Button>
                  )}
                  {r.status === "creditada" && (
                    <Button
                      data-testid={`use-credit-${r.id}`}
                      onClick={() => act(r.id, "use-credit")}
                      size="sm"
                      className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                      <CircleCheck className="mr-1.5 h-4 w-4" /> Crédito utilizado
                    </Button>
                  )}
                  <Button
                    data-testid={`notify-${r.id}`}
                    onClick={() => notify(r)}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-emerald-200 bg-emerald-50 text-[#1B7A45] hover:bg-emerald-100"
                  >
                    <MessageCircle className="mr-1.5 h-4 w-4" /> Avisar no WhatsApp
                  </Button>
                </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Faturamento por mês */}
        <div className="mt-8" data-testid="revenue-by-month">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#1B7A45]" />
            <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
              Faturamento por mês
            </h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Soma das reservas confirmadas (pagas) em cada mês.
          </p>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
            {revenueByMonth.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">
                Nenhuma reserva confirmada ainda.
              </p>
            ) : (
              <>
                <div className="divide-y divide-slate-100">
                  {revenueByMonth.map((m) => (
                    <div
                      key={m.month}
                      data-testid={`revenue-month-${m.month}`}
                      className="flex items-center justify-between py-2.5"
                    >
                      <span className="text-sm font-medium text-slate-700">{monthLabel(m.month)}</span>
                      <span className="font-display text-lg font-bold text-[#1B7A45]">{brl(m.total)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between border-t-2 border-slate-200 pt-3">
                  <span className="text-sm font-bold text-slate-800">Total geral</span>
                  <span className="font-display text-xl font-black text-[#E8611A]">{brl(revenueTotal)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <FloatingMenuButton />
    </div>
  );
}
